import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  getFaqItem,
  createFaq,
  updateFaq,
  handleClearFields,
  updateFaqTranslation,
  FaqTranslation,
} from "store/cms/pages/faq/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalFaqTabs } from "configs/types";

import Button from "components/buttons/Button";
import PagesFaqMainTab from "./tabs/PagesFaqMainTab";
import styles from "./CmsPagesFaqEditModal.module.scss";
import { Modal } from "components/Modal/Modal";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import PagesFaqItemsTab from "./tabs/PagesFaqItemsTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const CmsPagesFaqEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector((store) => store.cmsFaq.isItemFetched);
  const payload = useAppSelector((store) => store.cmsFaq.payload);
  const isItemFetching = useAppSelector((store) => store.cmsFaq.isItemFetching);

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);

  const [tabs] = useState<ModalFaqTabs[]>([
    ModalFaqTabs.main,
    ModalFaqTabs.faq,
  ]);
  const [activeTab, setActiveTab] = useState<ModalFaqTabs>(ModalFaqTabs.main);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [faqDraft, setFaqDraft] = useState<FaqTranslation | null>(null);

  const permissions = usePermissionHelper();
  const canSave = isEdit
    ? Boolean(permissions?.update)
    : Boolean(permissions?.create);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getFaqItem(entityId));
    }
  }, [dispatch, entityId, isItemFetched]);

  useEffect(() => {
    formErrors.resetErrors();
  }, [activeTab]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canSave) {
      return;
    }

    if (payload && activeTab === ModalFaqTabs.main) {
      if (!isEdit) {
        formErrors.resetErrors();
      }

      try {
        if (isEdit) {
          await dispatch(
            updateFaq({ ...payload, id: Number(entityId) }),
          ).unwrap();
        } else {
          const newId = await dispatch(createFaq(payload)).unwrap();
          navigate(`${location.pathname}?entityId=${newId}`);
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t(isEdit ? "entityUpdateSuccess" : "entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
      return;
    }

    if (activeTab === ModalFaqTabs.faq) {
      if (!faqDraft?.geo || !faqDraft?.locale) {
        return;
      }

      try {
        await dispatch(
          updateFaqTranslation({
            id: Number(entityId),
            data: {
              geo: faqDraft.geo,
              locale: faqDraft.locale,
              items: faqDraft.items.map((item) => ({
                title: item.title.trim(),
                description: item.description.trim(),
              })),
            },
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
    }
  };

  const handleTabClick = (tab: ModalFaqTabs) => {
    if (!isEdit && tab !== ModalFaqTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    setIsTabLoading(tab === ModalFaqTabs.faq);
  };

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const renderTab = () => {
    if (activeTab === ModalFaqTabs.main) {
      return (
        <PagesFaqMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canSave}
        />
      );
    }

    if (activeTab === ModalFaqTabs.faq) {
      return (
        <PagesFaqItemsTab
          id={entityId || ""}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!canSave}
          onLoadingChange={handleTabLoadingChange}
          onDraftChange={setFaqDraft}
        />
      );
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("faq")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
            canBeClicked={isItemFetched}
          />

          <Modal.Content
            className={`${styles.cmsPagesFaqContentWrapper} ${
              activeTab === ModalFaqTabs.faq ? styles.faqItemsContent : ""
            }`}
          >
            <div className="seo-content">{renderTab()}</div>
          </Modal.Content>

          <Modal.Footer>
            {canSave &&
              (isTabLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    isSecondary
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              ))}
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default CmsPagesFaqEditModal;
